import { LocalStorageService } from '@/shared/services/localStorage.service';
import { getStore } from '@/shared/lib/store/storeInstance';
import { rtkApi } from '@/shared/api';
import { routerPath } from '@/shared/config/router';
import { authActions } from '../..';
import type { IPasswordValidationResult } from '../../model/types/AuthSchema';

/**
 * Полная очистка auth данных, localStorage и кеша RTK Query
 * Единая точка для всех операций logout
 */
export const resetAuthState = () => {
  // 1. Очищаем токены
  LocalStorageService.clearAllTokens();

  if (typeof window !== 'undefined') {
    const keys = ['isAuth', 'user', 'userId', 'customRole', 'username', 'email'] as const;
    for (const key of keys) {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    }
  }

  // 3. Обновляем Redux state и очищаем кеш RTK Query
  const store = getStore();
  if (store) {
    store.dispatch(authActions.logout());
    store.dispatch(rtkApi.util.resetApiState());
  }
};

/**
 * Logout с редиректом на login страницу
 * Используется при 401/403 ошибках или когда профиль не загружается
 */
export const clearAuthAndRedirect = (redirectPath: string = '/login') => {
  resetAuthState();

  if (typeof window !== 'undefined' && window.location.pathname !== redirectPath) {
    window.location.href = redirectPath;
  }
};

export const resetSessionForActivationLink = () => {
  if (typeof window === 'undefined') return;

  const { pathname, search } = window.location;
  if (pathname !== routerPath.activate || !new URLSearchParams(search).get('token')) return;
  if (!LocalStorageService.getAccessTokenFromAnyStorage()) return;

  resetAuthState();
};

/**
 * Проверка требований пароля для отображения в UI
 * (с поддержкой латиницы и кириллицы для uppercase/lowercase)
 */
export const validatePasswordRequirements = (
  password: string,
  minLength: number = 8,
): IPasswordValidationResult => {
  const isMinLengthError = password ? password.length < minLength : false;
  const isUppercaseError = password ? !/[A-ZА-ЯЁ]/.test(password) : false;
  const isLowercaseError = password ? !/[a-zа-яё]/.test(password) : false;
  const isSpecialCharError = password
    ? !/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)
    : false;
  const isNumberError = password ? !/\d/.test(password) : false;

  const hasAnyError = Boolean(
    password &&
    (isMinLengthError ||
      isUppercaseError ||
      isLowercaseError ||
      isSpecialCharError ||
      isNumberError),
  );

  return {
    isMinLengthError,
    isUppercaseError,
    isLowercaseError,
    isSpecialCharError,
    isNumberError,
    hasAnyError,
  };
};
